import { Box, Card, CardContent, CircularProgress, Stack, Button, Chip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useBoolean from 'hooks/useBoolean';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import AppModal from 'components/AppModal';
import PopUpConfirmation from 'page-sections/submitted-abstract/PopUpConfirmation';
import downloadPreview from 'utils/downloadPreview';
import AbstractContentSection from './sections/AbstractContentSection';

const SubmittedAbstractDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const loading = useBoolean(true);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [updatedStatus, setUpdatedStatus] = useState('');
    const [disableButtonPopUp, setDisableButtonPopUp] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            loading.onTrue();
            try {
                const res = await axiosInstance.get(`api/prv/submissions/${id}`);
                setData(res.data);
            } catch (err) {
                console.log(err);
            } finally {
                loading.onFalse();
            }
        };

        fetchData();
    }, [id]);

    const canApprove = ['rejected', 'submitted'].includes(data?.status);
    const canReject = ['approved', 'submitted'].includes(data?.status);

    const handleDownload = async () => {
        if (!data) return;

        setDownloading(true);
        try {
            await downloadPreview(data);
        } catch (e) {
            console.log(e);
        } finally {
            setDownloading(false);
        }
    };

    const handleUpdateStatus = async (action) => {
        setDisableButtonPopUp(true);
        try {
            const status =
                action === 'Approve'
                    ? 'approved'
                    : action === 'Reject'
                    ? 'rejected'
                    : action;
            await axiosInstance.patch(`/api/prv/operator-submissions/${id}`, {
                status,
            });
            setOpenModal(false);
            navigate(-1);
        } catch (e) {
            console.log(e);
        } finally {
            setDisableButtonPopUp(false);
        }
    };

    return (
        <Box pt={3} pb={6} px={{ xs: 2, md: 4 }}>
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    {loading.value ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            py={10}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box>
                            <Chip
                                label={data?.status?.toUpperCase()}
                                color={
                                    data?.status === 'approved'
                                        ? 'success'
                                        : data?.status === 'rejected'
                                        ? 'error'
                                        : 'info'
                                }
                                sx={{
                                    marginBottom: 2,
                                }}
                            />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={3}
                            >
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </Button>
                                <Stack direction="row" spacing={1}>
                                    <LoadingButton
                                        variant="contained"
                                        color="info"
                                        onClick={handleDownload}
                                        loading={downloading}
                                    >
                                        Download Preview
                                    </LoadingButton>
                                    {canReject && 
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => {
                                                setOpenModal(true);
                                                setUpdatedStatus('Reject');
                                            }}
                                        >
                                            Reject
                                        </Button>
                                    }
                                    {canApprove && 
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={() => {
                                                setOpenModal(true);
                                                setUpdatedStatus('Approve');
                                            }}
                                        >
                                            Approve
                                        </Button>
                                    }
                                </Stack>
                            </Stack>
                            <AbstractContentSection data={data} />
                            <AppModal
                                open={openModal}
                                handleClose={() => setOpenModal(false)}
                            >
                                {data && (
                                    <PopUpConfirmation
                                        disableButton={disableButtonPopUp}
                                        status={updatedStatus}
                                        handleCancel={() => setOpenModal(false)}
                                        handleAction={handleUpdateStatus}
                                        data={data}
                                    />
                                )}
                            </AppModal>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SubmittedAbstractDetail;
